import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { cn } from '@/lib/utils';

type DrawerDirection = 'right' | 'left' | 'top' | 'bottom';
type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

function Drawer(props: {
	children: React.ReactNode;
	isOpen: boolean;
	close: () => void;
	direction: DrawerDirection;
	size: DrawerSize;
	transitionDuration?: number;
	fullScreen?: boolean;
	renderCloseButton?: (close: () => void) => React.ReactNode;
}) {
	const {
		children,
		isOpen,
		close,
		direction,
		size,
		transitionDuration = 200,
		fullScreen = false,
		renderCloseButton,
	} = props;
	const classes = getPositionClasses(direction, fullScreen);
	const sizeClasses = getSizeClasses(direction, size, fullScreen);
	const scrollDirection = getScrollDirection(direction);

	return (
		<Dialog
			style={{ '--default-transition-duration': `${transitionDuration}ms` } as React.CSSProperties}
			open={isOpen}
			onClose={close}
			className="relative z-10"
		>
			<DialogBackdrop
				transition
				className="fixed inset-0 bg-gray-500/50 transition-opacity ease-in-out data-closed:opacity-0"
			/>
			<div className="fixed inset-0 overflow-hidden">
				<div className="absolute inset-0 overflow-hidden">
					{/* --- Content + close button wrapper --- */}
					<div className={cn('pointer-events-none', classes.container)}>
						<DialogPanel
							transition
							className={cn(
								'pointer-events-auto relative transform transition-transform ease-in-out',
								sizeClasses,
								classes.panel,
							)}
						>
							{!fullScreen && !renderCloseButton && (
								<TransitionChild>
									<div className={cn('ease-in-out data-closed:opacity-0', classes.closeButton)}>
										<button
											type="button"
											onClick={close}
											className="relative rounded-md bg-black/30 text-white hover:bg-black/50 focus-visible:outline-2 focus-visible:outline-gray-800 focus-visible:outline-offset-2"
										>
											<span className="-inset-2.5 absolute" />
											<span className="sr-only">Close panel</span>
											<XMarkIcon aria-hidden="true" className="size-6" />
										</button>
									</div>
								</TransitionChild>
							)}
							<div
								className={cn(
									'relative h-full bg-white shadow-xl',
									scrollDirection,
									fullScreen && !renderCloseButton && 'pt-14',
								)}
							>
								{fullScreen && !renderCloseButton && (
									<button
										type="button"
										onClick={close}
										className="absolute top-4 right-4 z-10 rounded-md bg-white/80 p-2 text-gray-400 shadow-sm backdrop-blur-sm hover:bg-white hover:text-gray-500 focus-visible:outline-2 focus-visible:outline-gray-800 focus-visible:outline-offset-2"
									>
										<span className="sr-only">Close panel</span>
										<XMarkIcon aria-hidden="true" className="size-5" />
									</button>
								)}
								{renderCloseButton?.(close)}
								{children}
							</div>
						</DialogPanel>
					</div>
				</div>
			</div>
		</Dialog>
	);
}

// Size mapping for different directions
function getSizeClasses(dir: DrawerDirection, sz: DrawerSize, fullScreen: boolean) {
	// Full screen mode: take entire screen width/height
	// Use h-dvh for better mobile support (dynamic viewport height)
	if (fullScreen) {
		return 'w-screen h-dvh';
	}

	if (['right', 'left'].includes(dir)) {
		// @todo: these needs more consideration.
		// Width classes for horizontal drawers
		switch (sz) {
			case 'sm':
				return 'w-[calc(100vw-2.5rem)] max-w-xs'; // 320px
			case 'md':
				return 'w-[calc(100vw-2.5rem)] max-w-md'; // 448px
			case 'lg':
				return 'w-screen max-w-lg'; // 512px
			case 'xl':
				return 'w-screen max-w-xl'; // 576px
			case 'full':
				return 'w-screen'; // Full width
			default: {
				const _exhaustiveCheck: never = sz;
				return _exhaustiveCheck;
			}
		}
	}
	// Height classes for vertical drawers
	// Use h-dvh for better mobile support
	switch (sz) {
		case 'sm':
			return 'w-full h-dvh max-h-48'; // 192px
		case 'md':
			return 'w-full h-dvh max-h-96'; // 384px
		case 'lg':
			return 'w-full h-dvh max-h-[32rem]'; // 512px
		case 'xl':
			return 'w-full h-dvh max-h-[40rem]'; // 640px
		case 'full':
			return 'w-full h-dvh'; // Full height
		default: {
			const _exhaustiveCheck: never = sz;
			return _exhaustiveCheck;
		}
	}
}

// Direction-specific positioning classes
function getPositionClasses(dir: DrawerDirection, fullScreen: boolean) {
	// Full screen mode: still slide from direction but take full width
	if (fullScreen) {
		switch (dir) {
			case 'right':
				return {
					container: 'fixed inset-y-0 right-0 flex w-full',
					panel: 'data-closed:translate-x-full',
					closeButton: '', // Not used in fullscreen
				};
			case 'left':
				return {
					container: 'fixed inset-y-0 left-0 flex w-full',
					panel: 'data-closed:-translate-x-full',
					closeButton: '', // Not used in fullscreen
				};
			case 'top':
				return {
					container: 'fixed inset-x-0 top-0 flex w-full h-full',
					panel: 'data-closed:-translate-y-full',
					closeButton: '', // Not used in fullscreen
				};
			case 'bottom':
				return {
					container: 'fixed inset-x-0 bottom-0 flex w-full h-full',
					panel: 'data-closed:translate-y-full',
					closeButton: '', // Not used in fullscreen
				};
			default: {
				const _exhaustiveCheck: never = dir;
				return _exhaustiveCheck;
			}
		}
	}

	switch (dir) {
		case 'right':
			return {
				container: 'fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16',
				panel: 'data-closed:translate-x-full',
				closeButton: 'absolute top-0 left-0 -ml-8 flex pt-4 pr-2 sm:-ml-10 sm:pr-4',
			};
		case 'left':
			return {
				container: 'fixed inset-y-0 left-0 flex max-w-full pr-10 sm:pr-16',
				panel: 'data-closed:-translate-x-full',
				closeButton: 'absolute top-0 right-0 -mr-8 flex pt-4 pl-2 sm:-mr-10 sm:pl-4',
			};
		case 'top':
			return {
				container: 'fixed inset-x-0 top-0 flex w-full pb-10 sm:pb-16',
				panel: 'data-closed:-translate-y-full',
				closeButton: 'absolute bottom-0 right-0 -mb-8 flex pr-4 pt-2 sm:-mb-10 sm:pt-4',
			};
		case 'bottom':
			return {
				container: 'fixed inset-x-0 bottom-0 flex w-full pt-10 sm:pt-16',
				panel: 'data-closed:translate-y-full',
				closeButton: 'absolute top-0 right-0 -mt-8 flex pr-4 pb-2 sm:-mt-10 sm:pb-4',
			};
		default: {
			const _exhaustiveCheck: never = dir;
			return _exhaustiveCheck;
		}
	}
}

// Get scroll direction
function getScrollDirection(dir: DrawerDirection) {
	return ['top', 'bottom'].includes(dir) ? 'overflow-x-auto' : 'overflow-y-auto';
}

export { Drawer };
export type { DrawerDirection, DrawerSize };
